import { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formValueSelector, hasSubmitFailed } from 'redux-form';

import ThemedFormAddressInput from 'inquiry/components/InputAddress/ThemedFormAddressInput';
import { safeApply, useApply } from 'inquiry/lib/jsonLogic';
import { useShouldShowServerError } from 'inquiry/lib/serverError';
import { POSTAL_CODE_PRIMARY_COUNTRIES } from 'lib/constants/locales';
import { InputAddressComponent, UiComponentRule } from 'lib/interfaces/ui-components';
import { FormValue, Rule } from 'lib/utils/formValidation';
import isEmpty from 'lib/utils/formValidation/isEmpty';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';
import { RootState } from 'verify/redux/store';

interface InputAddressProps {
  component: InputAddressComponent;
  inquiry: Inquiry;
  inquiryLoading: boolean;
  actionsApi: UiComponentActionsApi;
}

const ADDRESS_FIELD_KEYS = {
  STREET_1: 'street_1',
  STREET_2: 'street_2',
  CITY: 'city',
  SUBDIVISION: 'subdivision',
  POSTAL_CODE: 'postal_code',
  BUSINESS_NAME: 'business_name',
};

const InputAddress: FC<InputAddressProps> = ({
  component,
  inquiry,
  inquiryLoading,
  actionsApi,
}: InputAddressProps) => {
  const fieldKeyAddressStreet1 = `${component.name}.${ADDRESS_FIELD_KEYS.STREET_1}`;
  const fieldKeyAddressStreet2 = `${component.name}.${ADDRESS_FIELD_KEYS.STREET_2}`;
  const fieldKeyAddressCity = `${component.name}.${ADDRESS_FIELD_KEYS.CITY}`;
  const fieldKeyAddressSubdivision = `${component.name}.${ADDRESS_FIELD_KEYS.SUBDIVISION}`;
  const fieldKeyAddressPostalCode = `${component.name}.${ADDRESS_FIELD_KEYS.POSTAL_CODE}`;
  const fieldKeyAddressBusinessName = `${component.name}.${ADDRESS_FIELD_KEYS.BUSINESS_NAME}`;

  const {
    // Prefills
    'prefill-address-street-1': prefillAddressStreet1,
    'prefill-address-street-2': prefillAddressStreet2,
    'prefill-address-city': prefillAddressCity,
    'prefill-address-subdivision': prefillAddressSubdivision,
    'prefill-address-postal-code': prefillAddressPostalCode,
    'prefill-address-business-name': prefillAddressBusinessName,
    'selected-country-code': selectedCountryCode,
    // Validations
    'validations-address-street-1': validationsAddressStreet1,
    'validations-address-street-2': validationsAddressStreet2,
    'validations-address-city': validationsAddressCity,
    'validations-address-subdivision': validationsAddressSubdivision,
    'validations-address-postal-code': validationsAddressPostalCode,
    'validations-address-business-name': validationsAddressBusinessName,
    // Translations
    'edit-address-manually-prompt': editAddressManuallyPrompt,
    label,
    'placeholder-autocomplete': placeholderAutocomplete,
    'placeholder-autocomplete-postal-code': placeholderAutocompletePostalCode,
    'placeholder-address-street-1': placeholderAddressStreet1,
    'placeholder-address-street-2': placeholderAddressStreet2,
    'placeholder-address-city': placeholderAddressCity,
    'placeholder-address-subdivision': placeholderAddressSubdivision,
    'placeholder-address-subdivision-us': placeholderAddressSubdivisionUs,
    'placeholder-address-postal-code': placeholderAddressPostalCode,
    'placeholder-address-postal-code-us': placeholderAddressPostalCodeUs,
    'placeholder-address-business-name': placeholderAddressBusinessName,
    // Errors
    'error-address-street-1': errorAddressStreet1,
    'error-address-street-2': errorAddressStreet2,
    'error-address-city': errorAddressCity,
    'error-address-subdivision': errorAddressSubdivision,
    'error-address-postal-code': errorAddressPostalCode,
    'error-address-business-name': errorAddressBusinessName,
    // other config
    'address-autocomplete-method': addressAutocompleteMethod,
    'format-address-subdivision': addressSubdivisionFormat,
    'place-type': placeType,
    // form configs
    disabled,
    hidden,
    optional,
    // validations,
  } = component.attributes;

  const formName = inquiry.attributes['next-step'].name;
  const submitFailed = useSelector(hasSubmitFailed(formName));
  // Don't count address as set if business name only is set
  const isAddressSet = useSelector<RootState, boolean>((state) =>
    [
      fieldKeyAddressStreet2,
      fieldKeyAddressCity,
      fieldKeyAddressSubdivision,
      POSTAL_CODE_PRIMARY_COUNTRIES.includes(selectedCountryCode)
        ? fieldKeyAddressStreet1
        : fieldKeyAddressPostalCode,
    ].some((attributeName) => formValueSelector(formName)(state, attributeName)),
  );

  const shouldShowServerErrorStreet1 = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyAddressStreet1,
    prefillAddressStreet1,
  );
  const shouldShowServerErrorStreet2 = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyAddressStreet2,
    prefillAddressStreet2,
  );
  const shouldShowServerErrorCity = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyAddressCity,
    prefillAddressCity,
  );
  const shouldShowServerErrorSubdivision = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyAddressSubdivision,
    prefillAddressSubdivision,
  );
  const shouldShowServerErrorPostalCode = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyAddressPostalCode,
    prefillAddressPostalCode,
  );
  const shouldShowServerErrorBusinessName = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyAddressBusinessName,
    prefillAddressBusinessName,
  );

  // if any address input is touched, clear all server errors
  const shouldShowServerError =
    shouldShowServerErrorStreet1 &&
    shouldShowServerErrorStreet2 &&
    shouldShowServerErrorCity &&
    shouldShowServerErrorSubdivision &&
    shouldShowServerErrorPostalCode &&
    shouldShowServerErrorBusinessName;

  const hasServerError = !!(
    errorAddressStreet1 ||
    errorAddressStreet2 ||
    errorAddressCity ||
    errorAddressSubdivision ||
    errorAddressPostalCode ||
    errorAddressBusinessName
  );

  // Does not 100% work. Will only use the "primary address field" for validations.
  // To fix, useApply will need to be able to account for a map of values.
  const apply = useApply('', inquiry.attributes['next-step'].name);
  const isHidden = apply<boolean>(hidden);

  const validateAddressStreet1 = useInputAddressValidators(
    component.name,
    validationsAddressStreet1,
  );
  const validateAddressStreet2 = useInputAddressValidators(
    component.name,
    validationsAddressStreet2,
  );
  const validateAddressCity = useInputAddressValidators(component.name, validationsAddressCity);
  const validateAddressSubdivision = useInputAddressValidators(
    component.name,
    validationsAddressSubdivision,
  );
  const validateAddressPostalCode = useInputAddressValidators(
    component.name,
    validationsAddressPostalCode,
  );
  const validateAddressBusinessName = useInputAddressValidators(
    component.name,
    validationsAddressBusinessName,
  );

  useEffect(() => {
    if (isHidden) {
      actionsApi.setField(fieldKeyAddressStreet1, '');
      actionsApi.setField(fieldKeyAddressStreet2, '');
      actionsApi.setField(fieldKeyAddressCity, '');
      actionsApi.setField(fieldKeyAddressSubdivision, '');
      actionsApi.setField(fieldKeyAddressPostalCode, '');
      actionsApi.setField(fieldKeyAddressBusinessName, '');
    } else if (!isHidden) {
      [
        [fieldKeyAddressStreet1, prefillAddressStreet1],
        [fieldKeyAddressStreet2, prefillAddressStreet2],
        [fieldKeyAddressCity, prefillAddressCity],
        [fieldKeyAddressSubdivision, prefillAddressSubdivision],
        [fieldKeyAddressPostalCode, prefillAddressPostalCode],
        [fieldKeyAddressBusinessName, prefillAddressBusinessName],
      ].forEach(([fieldKey, prefillValue]) => {
        if (!isEmpty(prefillValue)) {
          actionsApi.setField(fieldKey, prefillValue);
        }
      });
    }
  }, [isHidden]);

  if (isHidden) {
    return null;
  }

  return (
    <>
      <ThemedFormAddressInput
        disabled={apply<boolean>(disabled)}
        optional={apply<boolean>(optional?.logic)}
        // validate={validators}
        selectedCountryCode={selectedCountryCode}
        // field references
        fieldKeyAddressStreet1={fieldKeyAddressStreet1}
        fieldKeyAddressStreet2={fieldKeyAddressStreet2}
        fieldKeyAddressCity={fieldKeyAddressCity}
        fieldKeyAddressSubdivision={fieldKeyAddressSubdivision}
        fieldKeyAddressPostalCode={fieldKeyAddressPostalCode}
        fieldKeyAddressBusinessName={fieldKeyAddressBusinessName}
        // Translations
        editAddressManuallyPrompt={editAddressManuallyPrompt}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        label={label}
        placeholderAutocomplete={placeholderAutocomplete}
        placeholderAutocompletePostalCode={placeholderAutocompletePostalCode}
        placeholderAddressStreet1={placeholderAddressStreet1}
        placeholderAddressStreet2={placeholderAddressStreet2}
        placeholderAddressCity={placeholderAddressCity}
        placeholderAddressSubdivision={placeholderAddressSubdivision}
        placeholderAddressSubdivisionUs={placeholderAddressSubdivisionUs}
        placeholderAddressPostalCode={placeholderAddressPostalCode}
        placeholderAddressPostalCodeUs={placeholderAddressPostalCodeUs}
        placeholderAddressBusinessName={placeholderAddressBusinessName}
        validateAddressStreet1={validateAddressStreet1}
        validateAddressStreet2={validateAddressStreet2}
        validateAddressCity={validateAddressCity}
        validateAddressSubdivision={validateAddressSubdivision}
        validateAddressPostalCode={validateAddressPostalCode}
        validateAddressBusinessName={validateAddressBusinessName}
        // server errors
        serverErrorAddressStreet1={shouldShowServerError ? errorAddressStreet1 : undefined}
        serverErrorAddressStreet2={shouldShowServerError ? errorAddressStreet2 : undefined}
        serverErrorAddressCity={shouldShowServerError ? errorAddressCity : undefined}
        serverErrorAddressSubdivision={shouldShowServerError ? errorAddressSubdivision : undefined}
        serverErrorAddressPostalCode={shouldShowServerError ? errorAddressPostalCode : undefined}
        serverErrorAddressBusinessName={
          shouldShowServerError ? errorAddressBusinessName : undefined
        }
        // Other
        addressAutocompleteMethod={addressAutocompleteMethod}
        addressSubdivisionFormat={addressSubdivisionFormat}
        placeType={placeType}
        setField={actionsApi.setField}
        forceOpen={submitFailed || isAddressSet || hasServerError}
        showBusinessNameField={component.attributes['field-key-address-business-name'] != null}
        styles={component.styles}
      />
    </>
  );
};

export default InputAddress;

/**
 * InputAddress specific version of useValidators. We do this because
 * redux-form passes the value of the SUB-INPUT to the first argument of the
 * validator function, rather than the value of the InputAddress itself.
 */
export function useInputAddressValidators(
  componentName: string,
  validations: UiComponentRule[],
): Rule[] {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return useMemo(() => {
    const validationRules = (validations ?? []).map(
      ({ logic, message }) =>
        (_value: never, form: Record<string, FormValue>) =>
          !safeApply(
            logic,
            {
              form,
              // Redux-form tries to map this to the value of the subfield instead
              // of the value of the entire address input. Manually do this ourselves.
              value: form[componentName],
            },
            // fallback to true for invalid (or with server-side only operators) validations
            // (effectively ignoring them)
            true,
          ) && message,
    );

    return validationRules;
  }, [componentName, validations]);
}
